import { styled } from "goober";
import { useEffect } from "react";
import {
  isArchiveMediaType,
  isCsvMediaType,
  isSqliteMediaType,
} from "../../../shared/attachmentPolicy";
import { formatBytes } from "../../lib/formatBytes";
import { CsvPreview } from "../CsvPreview";
import { Text } from "../Text";
import { Archive } from "./Archive";
import { Audio } from "./Audio";
import { Binary } from "./Binary";
import { Email } from "./Email";
import { Html } from "./Html";
import { Http } from "./Http";
import { Image } from "./Image";
import { Json } from "./Json";
import { Markdown } from "./Markdown";
import { Pdf } from "./Pdf";
import { Plain } from "./Plain";
import type {
  FileRenderContent,
  FileRenderControl,
  FileSelection,
} from "./types";
import { useUrlContent, PREVIEW_BYTES } from "./useUrlContent";

export type {
  FileRenderContent,
  FileRenderControl,
  FileSelection,
} from "./types";
import { SqlitePreview } from "../SqlitePreview";
import { Video } from "./Video";

const TruncatedBanner = styled("div")`
  padding: 8px 12px;
  border-top: 1px solid var(--alpha8);
  background: var(--alpha4);
`;

const Empty = styled("div")`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-secondary);
`;

export type FileRenderProps = {
  url: string;
  fileName?: string;
  sizeBytes?: number;
  // The container's padding; full-bleed renderers (html, binary, http, json
  // table) cancel it to reach the container's edges.
  bleed?: number;
  baseRoute?: string;
  selectedRanges?: readonly { from: number; to: number; color?: string }[];
  emptyState?: React.ReactNode;
  onSelect?: (selections: FileSelection[]) => void;
  onControls?: (controls: FileRenderControl[]) => void;
  // Surfaces the inline text this renderer read (null for binary/streamed
  // types), so external chrome can back a copy button off the same single fetch.
  onContent?: (content: FileRenderContent | null) => void;
};

export function FileRender({
  url,
  fileName,
  sizeBytes,
  bleed,
  baseRoute,
  selectedRanges,
  emptyState,
  onSelect,
  onControls,
  onContent,
}: FileRenderProps) {
  const { loading, contentType, content, size, truncated } = useUrlContent(url);
  const totalSize = size ?? sizeBytes;

  useEffect(() => {
    onContent?.(content != null ? { text: content, truncated } : null);
  }, [onContent, content, truncated]);

  if (loading || !contentType) return null;

  if (contentType.startsWith("image/")) {
    return <Image url={url} contentType={contentType} fileName={fileName} />;
  }
  if (contentType.startsWith("audio/")) {
    return <Audio url={url} contentType={contentType} fileName={fileName} />;
  }
  if (contentType.startsWith("video/")) {
    return <Video url={url} contentType={contentType} fileName={fileName} />;
  }
  if (contentType === "application/pdf") {
    return <Pdf url={url} fileName={fileName} />;
  }
  if (contentType === "message/http") {
    return (
      <Http
        url={url}
        bleed={bleed}
        onControls={onControls}
        onSelect={onSelect}
        selectedRanges={selectedRanges}
      />
    );
  }
  if (contentType === "message/rfc822") {
    return <Email url={url} bleed={bleed} onControls={onControls} />;
  }
  if (isCsvMediaType(contentType)) {
    return (
      <CsvPreview
        enabled
        sizeBytes={totalSize ?? 0}
        url={url}
        fileName={fileName}
      />
    );
  }
  if (isSqliteMediaType(contentType)) {
    return <SqlitePreview enabled url={url} />;
  }
  if (isArchiveMediaType(contentType)) {
    return (
      <Archive
        url={url}
        contentType={contentType}
        fileName={fileName}
        bleed={bleed}
        onControls={onControls}
      />
    );
  }

  if (content == null) {
    return (
      <Binary
        url={url}
        contentType={contentType}
        fileName={fileName}
        sizeBytes={size || sizeBytes}
      />
    );
  }

  if (content === "") return emptyState ?? <Empty>Empty file</Empty>;

  const body =
    contentType === "application/json" ||
    contentType === "application/jsonl" ? (
      <Json
        output={content}
        bleed={bleed}
        selectedRanges={selectedRanges}
        onSelect={onSelect}
        onControls={onControls}
      />
    ) : contentType === "text/markdown" ? (
      <Markdown
        output={content}
        selectedRanges={selectedRanges}
        onSelect={onSelect}
        onControls={onControls}
      />
    ) : contentType === "text/html" ? (
      <Html output={content} bleed={bleed} baseRoute={baseRoute} />
    ) : (
      <Plain
        output={content}
        selectedRanges={selectedRanges}
        onSelect={onSelect}
      />
    );

  return (
    <>
      {body}
      {truncated && totalSize != null && (
        <TruncatedBanner>
          <Text variant="text-small" color="var(--text-secondary)">
            Content truncated – showing first {formatBytes(PREVIEW_BYTES)} of{" "}
            {formatBytes(totalSize)}
          </Text>
        </TruncatedBanner>
      )}
    </>
  );
}
